import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useRef, Suspense, useState, useEffect } from 'react';

var _excluded = ["fallback", "Component"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/**
 * This method is derived from https://dev.to/anxinyang/easy-lazy-loading-with-react-intersection-observer-api-1dll
 * Certain elements of the original code in the example do not function correctly, and hence have been adapted here
 * Primarily this refers to typesetting which was implemented in the origial code
 *
 * */
// Function to determine if a component is visible or not on the screen

function useIntersectionObserver(ref, options) {
  var forward = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      element = _useState2[0],
      setElement = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isIntersecting = _useState4[0],
      setIsIntersecting = _useState4[1];

  var observer = useRef(null);

  var cleanOb = function cleanOb() {
    if (observer.current) {
      observer.current.disconnect();
    }
  };

  useEffect(function () {
    setElement(ref.current);
  }, [ref]);
  useEffect(function () {
    if (!element) return;
    cleanOb();
    var ob = observer.current = new IntersectionObserver(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 1),
          entry = _ref2[0];

      var isElementIntersecting = entry.isIntersecting;

      if (!forward) {
        setIsIntersecting(isElementIntersecting);
      } else if (forward && !isIntersecting && isElementIntersecting) {
        setIsIntersecting(isElementIntersecting);
        cleanOb();
      }
    }, _objectSpread({}, options));
    ob.observe(element);
    return function () {
      cleanOb();
    };
  }, [element, options]);
  return isIntersecting;
}
/*
interface LazyLoadProps {
        tag?: ComponentType | keyof JSX.IntrinsicElements
        children: ReactNode
        style?: CSSProperties
        className?: string
        root?: Element | Document | null
        threshold?: number | number[]
        rootMargin?: string
        forward?: boolean
}
    */
// Wrapper component to allow lazy loading of any component
// The props for this function can be seen in the commented out interface called LazyLoadProps

function LazyLoad(props) {
  var _props$root, _props$threshold;

  var _props$tag = props.tag,
      tag = _props$tag === void 0 ? "div" : _props$tag,
      children = props.children,
      style = props.style,
      className = props.className;
  var Tag = tag;
  var ref = useRef(null);
  var isIntersecting = useIntersectionObserver(ref, {
    root: (_props$root = props.root) !== null && _props$root !== void 0 ? _props$root : null,
    threshold: (_props$threshold = props.threshold) !== null && _props$threshold !== void 0 ? _props$threshold : 0,
    rootMargin: props.rootMargin
  }, props.forward);

  if (isIntersecting) {
    return /*#__PURE__*/React.createElement(Tag, {
      style: style,
      className: className,
      children: isIntersecting ? children : null
    });
  } else {
    return /*#__PURE__*/React.createElement("div", {
      ref: ref,
      style: {
        height: "100vh",
        width: "100%"
      }
    }, ".");
  }
} // Wrapper component to allow lazy loading of any component
// The props for this function can be seen in the commented out interface called LazyLoadProps

function LazyComponent(props) {
  var _props$root2, _props$threshold2;

  var _props$fallback = props.fallback,
      fallback = _props$fallback === void 0 ? null : _props$fallback,
      Component = props.Component,
      componentProps = _objectWithoutProperties(props, _excluded);

  var ref = useRef(null);
  var isIntersecting = useIntersectionObserver(ref, {
    root: (_props$root2 = props.root) !== null && _props$root2 !== void 0 ? _props$root2 : null,
    threshold: (_props$threshold2 = props.threshold) !== null && _props$threshold2 !== void 0 ? _props$threshold2 : 0,
    rootMargin: props.rootMargin
  }, props.forward);

  if (props.blockShow != true || isIntersecting) {
    return /*#__PURE__*/React.createElement(Suspense, {
      fallback: fallback
    }, /*#__PURE__*/React.createElement(Component, componentProps));
  } else {
    return /*#__PURE__*/React.createElement("div", {
      ref: ref,
      style: {
        height: "100vh",
        width: "100%"
      }
    }, ".");
  }
}

export { LazyLoad, LazyComponent as default, useIntersectionObserver };
